import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { getViewers, addViewer, deleteViewer, setViewerPassword } from "@/lib/api";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, KeyRound, Loader2 } from "lucide-react";

const inputCls =
  "rounded-none border-[#0A1F44]/30 bg-transparent focus-visible:ring-1 focus-visible:ring-[#C0982E] focus-visible:border-[#C0982E] font-mono";

const isValidPw = (pw) => pw.length >= 8 && /\d/.test(pw) && /[a-zA-Z]/.test(pw);

export const ManageAccessDialog = ({ open, onOpenChange }) => {
  const [viewers, setViewers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setViewers(await getViewers()); }
    catch { toast.error("Could not load the access list"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { load(); setNewEmail(""); setNewPw(""); } }, [open, load]);

  const add = async (e) => {
    e?.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    try {
      const v = await addViewer(newEmail.trim());
      setViewers((cur) => [...cur, v].sort((a, b) => a.email.localeCompare(b.email)));
      setNewEmail("");
      toast.success(`Added ${v.email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add that email");
    } finally { setAdding(false); }
  };

  const remove = async (id, email) => {
    try {
      await deleteViewer(id);
      setViewers((cur) => cur.filter((v) => v.id !== id));
      toast.success(`Removed ${email}`);
    } catch {
      toast.error("Could not remove that email");
    }
  };

  const savePw = async (e) => {
    e?.preventDefault();
    if (!isValidPw(newPw)) {
      toast.error("Password must be at least 8 characters and include a number and a letter");
      return;
    }
    setSavingPw(true);
    try {
      await setViewerPassword(newPw);
      setNewPw("");
      toast.success("Shared coworker password updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update the password");
    } finally { setSavingPw(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manage-access-dialog" className="rounded-none border border-[#0A1F44] bg-[#F3F5F8] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#0A1F44] flex items-center gap-2">
            <Users className="w-5 h-5 text-[#C0982E]" strokeWidth={1.5} /> Manage Access
          </DialogTitle>
          <DialogDescription className="text-[#0A1F44]/60">
            Pre-register coworkers by email. They sign in with their email and the shared password below.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-6">
          <form onSubmit={add} className="space-y-2">
            <Label className="text-xs uppercase tracking-wider">Add coworker email</Label>
            <div className="flex gap-2">
              <Input data-testid="viewer-email-input" type="email" className={inputCls} value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="coworker@neisd.net" />
              <Button type="submit" data-testid="viewer-add-btn" disabled={adding} className="rounded-none bg-[#0A1F44] text-[#F3F5F8] hover:bg-[#C0982E] gap-1.5 shrink-0">
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Add
              </Button>
            </div>
          </form>

          <div className="border border-[#0A1F44]/20">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#0A1F44]/50 px-4 py-2 bg-[#E6EAF1] flex justify-between">
              <span>Coworkers with access</span>
              <span data-testid="viewer-count">{viewers.length}</span>
            </div>
            {loading ? (
              <div className="p-4 text-sm text-[#0A1F44]/50 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : viewers.length === 0 ? (
              <div data-testid="viewer-empty" className="p-4 text-sm text-[#0A1F44]/50">No coworkers added yet. Only the administrator can sign in.</div>
            ) : (
              <ul data-testid="viewer-list" className="divide-y divide-[#0A1F44]/10 max-h-56 overflow-y-auto">
                {viewers.map((v) => (
                  <li key={v.id} data-testid={`viewer-row-${v.email}`} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="font-mono text-[#0A1F44] break-all">{v.email}</span>
                    <button
                      data-testid={`viewer-delete-${v.email}`}
                      onClick={() => remove(v.id, v.email)}
                      className="text-[#b3402a] hover:bg-[#b3402a]/10 p-1.5 shrink-0"
                      title={`Remove ${v.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <form onSubmit={savePw} className="space-y-2 border-t border-[#0A1F44]/15 pt-5">
            <Label className="text-xs uppercase tracking-wider flex items-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5 text-[#C0982E]" /> Shared coworker password
            </Label>
            <p className="text-[11px] text-[#0A1F44]/50">All coworkers sign in with this one password. At least 8 characters incl. a number and a letter.</p>
            <div className="flex gap-2">
              <Input data-testid="viewer-password-input" type="text" className={inputCls} value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New shared password" />
              <Button type="submit" data-testid="viewer-password-save" disabled={savingPw} className="rounded-none bg-[#C0982E] text-[#F3F5F8] hover:bg-[#0A1F44] shrink-0">
                {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update"}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};
